import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, LinearProgress, TextField } from "@material-ui/core";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import useStyles from "./styles";
import CrmService, { apiError, ymdInTz } from "./crmService";
import { EmptyState, PageHead, useCrmSettings } from "./crmUi";

const INK = "#3b3b3b";

function ChartTip({ active, payload, unit }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{ background: "#1a1a1a", color: "#fff", fontSize: 12, padding: "6px 10px", borderRadius: 6 }}>
      {p.name}: <strong>{p.count}</strong> {unit}
    </div>
  );
}

/** One series of counts as horizontal bars (single hue, labels in ink). */
function HBars({ title, note, data, unit, empty }) {
  const classes = useStyles();
  const rows = data || [];
  const height = Math.max(120, rows.length * 34 + 30);
  return (
    <section className={classes.panel}>
      <h2 className={classes.panelTitle}>{title}</h2>
      {note && <div className={classes.muted} style={{ marginTop: 4 }}>{note}</div>}
      {rows.length === 0 ? (
        <div className={classes.muted} style={{ padding: "32px 0", textAlign: "center" }}>
          {empty}
        </div>
      ) : (
        <>
          <div style={{ height, marginTop: 12 }} aria-hidden>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 28, bottom: 0, left: 0 }} barCategoryGap={10}>
                <CartesianGrid horizontal={false} stroke="#ececec" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#555" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 13, fill: "#1a1a1a" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f5f5f5" }} content={<ChartTip unit={unit} />} />
                <Bar
                  dataKey="count"
                  fill={INK}
                  radius={[0, 4, 4, 0]}
                  maxBarSize={18}
                  label={{ position: "right", fontSize: 12, fill: "#1a1a1a" }}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Screen-reader table of the same numbers */}
          <table style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
            <caption>{title}</caption>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name}>
                  <th scope="row">{r.name}</th>
                  <td>{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

/** Admissions dashboard for one month (school time zone). */
export default function CrmDashboard() {
  const classes = useStyles();
  const history = useHistory();
  const { tz } = useCrmSettings();
  const [month, setMonth] = useState(() => ymdInTz(new Date(), tz).slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await CrmService.dashboard(month));
    } catch (err) {
      setError(apiError(err, "Could not load the dashboard.").message);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals;
  const monthLabel = (() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  })();
  const tiles = t
    ? [
        { label: "New enquiries", value: t.newEnquiries, note: "received this month" },
        { label: "Tours", value: t.tours, note: "families visited" },
        { label: "Admissions", value: t.admissions, note: "marked Admitted", dot: "#1f8a5b" },
        { label: "Lost", value: t.lost, note: "closed as Lost", dot: "#b3261e" },
        { label: "Conversion rate", value: `${t.conversionRate}%`, note: "admissions ÷ new enquiries" },
        { label: "Follow-ups done", value: t.followUps, note: "calls, visits, messages" },
      ]
    : [];
  const pipelineMax = Math.max(1, ...(data?.pipeline || []).map((p) => p.count));

  return (
    <div className={classes.page}>
      <PageHead
        title="Admissions dashboard"
        subtitle={monthLabel}
        actions={
          <TextField
            type="month"
            label="Month"
            value={month}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        }
      />
      {loading && <LinearProgress style={{ marginBottom: 12 }} />}
      {error && !loading ? (
        <div className={classes.panel}>
          <EmptyState
            title="Could not load the dashboard"
            text={error}
            action={
              <Button variant="contained" color="primary" onClick={load}>
                Retry
              </Button>
            }
          />
        </div>
      ) : (
        data && (
          <>
            <div className={classes.tiles}>
              {tiles.map((tile) => (
                <div key={tile.label} className={`${classes.tile} ${classes.tileStatic}`}>
                  <div className={classes.tileLabel}>
                    {tile.dot && <span className={classes.dot} style={{ background: tile.dot }} />}
                    {tile.label}
                  </div>
                  <div className={classes.tileValue}>{tile.value}</div>
                  <div className={classes.tileNote}>{tile.note}</div>
                </div>
              ))}
            </div>

            <div className={classes.charts}>
              <HBars title="Enquiries by source" data={data.bySource} unit="enquiries" empty="No enquiries this month." />
              <HBars title="Admissions by source" data={data.admissionsBySource} unit="admissions" empty="No admissions this month." />
              <HBars title="Lost reasons" data={data.lostReasons} unit="lost" empty="No enquiries lost this month." />
              <HBars
                title="Follow-ups by staff"
                data={data.staffFollowUps}
                unit="follow-ups"
                empty="No follow-ups logged this month."
              />
              <section className={classes.panel}>
                <h2 className={classes.panelTitle}>Pipeline </h2>
                <div className={classes.muted} style={{ marginTop: 4 }}>
                  All enquiries by current status. Select one to see the list.
                </div>
                <div className={classes.funnel}>
                  {data.pipeline.map((p) => (
                    <button
                      key={p.name}
                      type="button"
                      className={classes.funnelRow}
                      style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit", textAlign: "left" }}
                      onClick={() => history.push(`/app/crm/enquiries?status=${encodeURIComponent(p.name)}`)}
                    >
                      <span>{p.name}</span>
                      <span className={classes.funnelTrack}>
                        <span
                          className={classes.funnelBar}
                          style={{ display: "block", width: `${(p.count / pipelineMax) * 100}%`, opacity: p.count ? 1 : 0 }}
                        />
                      </span>
                      <strong style={{ fontWeight: 600, textAlign: "right" }}>{p.count}</strong>
                    </button>
                  ))}
                </div>
              </section>
            </div>
          </>
        )
      )}
    </div>
  );
}
